import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Activity, HeartPulse, Ruler, Target, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { listPatients } from '@/features/patients/patients-api';
import { getCurrentAnamnesis } from '@/features/anamnesis/anamnesis-api';
import { PATIENT_ROUTE_TABS } from '@/features/patients/patient-tabs';

export function PatientContextLayout() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { t } = useTranslation();

  const patientQuery = useQuery({
    queryKey: ['patient', id, 'context'],
    queryFn: ({ signal }) => listPatients({ page: 1, pageSize: 20, search: id }, signal),
    enabled: !!id,
    staleTime: 60_000,
  });
  const anamnesisQuery = useQuery({
    queryKey: ['anamnesis-current', id, 'context'],
    queryFn: ({ signal }) => getCurrentAnamnesis(id ?? '', signal),
    enabled: !!id,
    staleTime: 60_000,
  });

  if (!id) return null;
  const base = `/patients/${id}`;
  if (location.pathname === base) return <Outlet />;

  const patient = patientQuery.data?.rows.find((p) => p.id === id);
  const a = anamnesisQuery.data;
  const name = a?.name || patient?.name || 'Paciente';
  const allergies = (a?.allergies ?? []).filter(Boolean);
  const conditions = [...(a?.diseases ?? []), ...(a?.pathologies ?? [])].filter(Boolean);
  const medications = (a?.medications ?? []).filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <nav aria-label={t('patients.title')}>
          <ul role="tablist" aria-orientation="vertical" className="flex gap-1 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-col lg:overflow-visible lg:pb-0">
            <li>
              <Link
                to={base}
                className="inline-flex border-b-2 border-transparent px-3 py-2 text-sm font-medium text-text-muted transition-colors hover:text-text lg:w-full lg:border-b-0 lg:border-l-2"
              >
                Visão geral
              </Link>
            </li>
            {PATIENT_ROUTE_TABS.map((tab) => {
              const href = tab.href(id);
              const active = location.pathname === href;
              return (
                <li key={tab.key} className="shrink-0 lg:shrink">
                  <Link
                    to={href}
                    role="tab"
                    aria-selected={active}
                    className={cn(
                      'inline-flex w-full items-center whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors lg:border-b-0 lg:border-l-2 lg:text-left',
                      active ? 'border-action font-semibold text-action' : 'border-transparent text-text-muted hover:border-border hover:text-text',
                    )}
                  >
                    {t(tab.i18n)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="min-w-0 space-y-4">
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-text">{name}</h1>
              <p className="mt-0.5 text-xs text-text-muted">
                {a ? `Anamnese v${a.version}` : 'Anamnese pendente'}
                {a?.age ? ` • ${a.age} anos` : ''}
                {a?.sex === 'female' ? ' • Feminino' : a?.sex === 'male' ? ' • Masculino' : ''}
              </p>
              {a && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
                  <InlineMetric icon={Ruler} text={[a.weight && `${a.weight} kg`, a.height && `${a.height} cm`].filter(Boolean).join(' • ') || 'Peso/altura —'} />
                  <InlineMetric icon={Activity} text={a.activity || 'Atividade —'} />
                  <InlineMetric icon={Target} text={a.objectives?.join(', ') || 'Objetivo —'} />
                  {allergies.length > 0 && <InlineMetric icon={AlertTriangle} text={`Alergias: ${allergies.join(', ')}`} tone="danger" />}
                  {conditions.length > 0 && <InlineMetric icon={HeartPulse} text={`Condições: ${conditions.join(', ')}`} />}
                  {medications.length > 0 && <InlineMetric icon={HeartPulse} text={`Medicamentos: ${medications.join(', ')}`} />}
                </div>
              )}
            </div>
            <Link to={`${base}/anamnesis`} className="text-xs font-medium text-action underline">
              {a ? 'Ver anamnese' : 'Registrar anamnese'}
            </Link>
          </header>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function InlineMetric({ icon: Icon, text, tone }: { icon: LucideIcon; text: string; tone?: 'danger' }) {
  return (
    <span className={cn('inline-flex max-w-full items-center gap-1', tone === 'danger' && 'text-danger-700')}>
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{text}</span>
    </span>
  );
}
